import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { StyledModal, StyledModalHeader, Notification } from '@shoflo/polymer';

import useIsMobile from 'hooks/useIsMobile';
import { AWAITING_KNOCK_APPROVAL } from 'constants/ModalTypes';
import useKnockRequest from 'hooks/useKnockRequest';
import checkRealtimeSocketConnection from 'actions/CheckRealtimeSocketConnection';
import { setKnockFailed, cancelPendingRequest } from 'actions/Broadcast';
import { useGlobalBeforeUnload, useGlobalUnload } from 'hooks/useWindowEvent';
import { StyledModalBody } from './AwaitingKnockApproval.styled';
import DelayedKnockWarning from './DelayedKnockWarning/DelayedKnockWarning';
import { nanoid } from 'nanoid';
import { StudioLogger } from '@studio/logger';

export const LOG = new StudioLogger('awaiting-knock-approval');

const AwaitingKnockApproval = () => {
  const { modal, realtimeSocketConnected, knockFailed, user } = useSelector(
    ({ modal, realtimeSocketConnected, knockFailed, user }) => ({
      modal,
      realtimeSocketConnected,
      knockFailed,
      user
    })
  );

  const isOpen = modal?.type === AWAITING_KNOCK_APPROVAL;
  const dispatch = useDispatch();

  const { knock } = useSelector(({ knock }) => ({
    knock
  }));
  const knockRejected = knock?.rejected;
  const knockErrored = knock?.errored;

  const isMobile = useIsMobile();
  const knockRequest = useKnockRequest();

  const knockRequestPayload = {
    presenterName: modal?.initialValue,
    isTransient: true,
    presenterDescription: '',
    presenterDetailsUpdated: false,
    sessionId: nanoid(10),
    presenterPhoto: user?.photo
  };

  const handleRetryKnock = () => {
    knockRequest(knockRequestPayload);
  };

  const clearDelayedKnockWarning = () => {
    dispatch(setKnockFailed(false));
  };

  const handlePageReloadOrClose = () => {
    dispatch(cancelPendingRequest(knock));
  };

  useGlobalBeforeUnload(handlePageReloadOrClose);
  useGlobalUnload(handlePageReloadOrClose);

  useEffect(() => {
    dispatch(checkRealtimeSocketConnection());
  }, [dispatch]);

  useEffect(() => {
    if (knockErrored) {
      LOG.error(
        `presenter ${knockRequestPayload.presenterName} knock request failed`
      );
    }
  }, [knockErrored, knockRequestPayload.presenterName]);

  useEffect(() => {
    if (knockRequestPayload.presenterName && isOpen) {
      LOG.info(
        `Knock request ${
          knockRejected ? 'rejected' : 'awaiting approval'
        } for presenter: ${knockRequestPayload.presenterName}`,
        { presenterName: knockRequestPayload.presenterName }
      );
    }
  }, [knockRejected, knockRequestPayload.presenterName]);

  return (
    <StyledModal
      data-testId="waiting-modal"
      isOpen={isOpen && !knockRejected}
      fade={!isMobile}
      tabIndex={-1}
    >
      <StyledModalHeader>
        <b>Waiting for the host...</b>
      </StyledModalHeader>

      <StyledModalBody>
        <p>We're waiting for the host to let you into Studio.</p>
        <br />
        <p>
          <i>
            Note: You will be able to control your mic and camera. The host is
            able to remotely enable your mic and camera if needed.
          </i>
        </p>
        {knockErrored && (
          <Notification
            type="warning"
            onClick={handleRetryKnock}
            message="Failed to send request."
            linkMessage="Try again"
            isDismissible={false}
          />
        )}
        {!realtimeSocketConnected && (
          <Notification
            type="warning"
            message="Realtime connection lost. Attempting to re-connect..."
            isDismissible={false}
          />
        )}
        {knockFailed && (
          <DelayedKnockWarning
            handleRetryKnock={handleRetryKnock}
            clearDelayedKnockWarning={clearDelayedKnockWarning}
          />
        )}
      </StyledModalBody>
    </StyledModal>
  );
};

export default AwaitingKnockApproval;
